import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Referrals from "@/pages/Referrals";
import Profile from "@/pages/Profile";
import FundDepositQR from "@/pages/FundDepositQR";
import FundDepositBank from "@/pages/FundDepositBank";
import Products from "@/pages/Products";
import RankStatus from "@/pages/RankStatus";
import DirectIncome from "@/pages/DirectIncome";
import LevelTeam from "@/pages/LevelTeam";
import Genealogy from "@/pages/Genealogy";
import KYC from "@/pages/KYC";
import FundTransfer from "@/pages/FundTransfer";
import RewardClubs from "@/pages/RewardClubs";
import AdminKYC from "@/pages/AdminKYC";
import AdminWithdrawals from "@/pages/AdminWithdrawals";
import Rewards from "@/pages/Rewards";
import LevelIncome from "@/pages/LevelIncome";
import RankIncome from "@/pages/RankIncome";
import DailyReferralBonus from "@/pages/DailyReferralBonus";
import MedicalInsurance from "@/pages/MedicalInsurance";
import AccidentalInsurance from "@/pages/AccidentalInsurance";
import EbookGallery from "@/pages/EbookGallery";
import AdminEbooks from "@/pages/AdminEbooks";
import AdminOffersTicker from "@/pages/AdminOffersTicker";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUserDetail from "@/pages/AdminUserDetail";
import AdminDeposits from "@/pages/AdminDeposits";
import AdminProducts from "@/pages/AdminProducts";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/admin/dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/referrals"
            element={
              <ProtectedRoute>
                <Referrals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/kyc"
            element={
              <ProtectedRoute>
                <KYC />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fund-deposit/qr"
            element={
              <ProtectedRoute>
                <FundDepositQR />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fund-deposit/bank"
            element={
              <ProtectedRoute>
                <FundDepositBank />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute>
                <Products />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fund-transfer"
            element={
              <ProtectedRoute>
                <FundTransfer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reward-clubs"
            element={
              <ProtectedRoute>
                <RewardClubs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rank-status"
            element={
              <ProtectedRoute>
                <RankStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/direct-income"
            element={
              <ProtectedRoute>
                <DirectIncome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/level"
            element={
              <ProtectedRoute>
                <LevelTeam />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/genealogy"
            element={
              <ProtectedRoute>
                <Genealogy />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rewards"
            element={
              <ProtectedRoute>
                <Rewards />
              </ProtectedRoute>
            }
          />
          <Route
            path="/level-income"
            element={
              <ProtectedRoute>
                <LevelIncome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rank-income"
            element={
              <ProtectedRoute>
                <RankIncome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/daily-referral-bonus"
            element={
              <ProtectedRoute>
                <DailyReferralBonus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insurance/medical"
            element={
              <ProtectedRoute>
                <MedicalInsurance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insurance/accidental"
            element={
              <ProtectedRoute>
                <AccidentalInsurance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/deposits"
            element={
              <AdminRoute>
                <AdminDeposits />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <AdminRoute>
                <AdminProducts />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/kyc"
            element={
              <AdminRoute>
                <AdminKYC />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/withdrawals"
            element={
              <AdminRoute>
                <AdminWithdrawals />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users/:code"
            element={
              <AdminRoute>
                <AdminUserDetail />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/ebooks"
            element={
              <AdminRoute>
                <AdminEbooks />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/offers-ticker"
            element={
              <AdminRoute>
                <AdminOffersTicker />
              </AdminRoute>
            }
          />
          <Route
            path="/ebooks"
            element={
              <ProtectedRoute>
                <EbookGallery />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
